import React, { useState } from "react";
import { useQueryClient } from 'react-query';
import { useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { useSnackbar } from "notistack";
import moment from "moment";
import 'react-datepicker/dist/react-datepicker.css';

import AssignDevicePanel from "containers/Dashboard/AssignDevicePanel";
import HereMap from "components/Map/HereMap";
import { Row, Column } from "components/Layout";
import WeatherWidget from "components/WeatherWidget";
import NTMTable from "components/Table/NTMTable";
import Image from "components/Image";
import EditLabelModal from "components/Modal/EditLabelModal";
import SolarSiteModal from "components/Modal/SolarSiteModal";
import NTMButton from "components/Buttons/NTMButton";

import { deleteElem, DEVICES, getErrorMessage, post, PROJECTS, put} from "../../service/Client";
import { useGetDevices } from "service/AgrismartService";
import { useGetSiteList, useGetUnassignedPlantList } from "service/SolarsmartService";
import { roles } from "../../constants";
import { addIcon_top, addIcon_topOver } from 'img/icons';
import { deleteIcon, deleteIcon_over, editIcon, editIcon_over, pinIcon } from 'img/icons'
import style from "containers/Dashboard/style.module.scss"


export default function DashboardPage() {

	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	let project = useSelector(store => store.session.project);
	let user = useSelector(store => store.users.data);
	let { devices } = useGetDevices()
	let { sites } = useGetSiteList()
	let { unassignedPlants } = useGetUnassignedPlantList()
	const [deviceModalOpen, setDeviceModalOpen] = useState()
	const [siteModalOpen, setSiteModalOpen] = useState(false)
	const [siteFields, setSiteFields] = useState()

	function showError(e) {
		enqueueSnackbar(getErrorMessage(e), {variant: "error"});
	}

	function changeLabel(label) {
		post(`${DEVICES}/${deviceModalOpen}/label`, {body: label, headers: {"Content-Type": "text/plain"}})
			.then(() => queryClient.invalidateQueries('siteList'))
			.catch(showError);
		setDeviceModalOpen(null);
	}

	function saveSite(siteProps) {
		setSiteModalOpen(false)
		setSiteFields(null)
		let request = siteProps.id ? put(`solarsmart/site/${siteProps.id}`, { body: siteProps })
									: post(`solarsmart/site`, { body: siteProps })
		request.then(() => {
			queryClient.invalidateQueries('siteList')
			queryClient.invalidateQueries('plantUnassignedList')
		})
			.catch(showError);
		setSiteModalOpen(null);
	}

	function deleteSite(siteId) {
		deleteElem(`solarsmart/site/${siteId}`)
			.then(() => {
				queryClient.invalidateQueries('siteList')
				queryClient.invalidateQueries('plantUnassignedList')
			})
			.catch(showError);
	}

	function isDeviceAssociatedToASite(device, sites) {
		if (!device) return false;
		return !!sites.find(a => a.device.id === device.id)
	}

	function disassociateDevice(device) {
		let promises = [
			put(`${PROJECTS}/${project.id}/devices`, {
				body: devices.filter(d => d.id !== device.id).map(d => d.device)
			}),
		]
		if (isDeviceAssociatedToASite(device, sites)) {
			promises.push(
				deleteElem(`solarsmart/site/${device.id}`)
			)
		}
		Promise.all(promises)
			.then(() => {
				queryClient.invalidateQueries('devices')
				queryClient.invalidateQueries('siteList')
				queryClient.invalidateQueries('plantUnassignedList')
			})
			.catch(showError);
	}

	function devicePopUp(siteId, deviceId) {
		return `<div class="bubble">
					<div class="name">id sito</div><div>${siteId}</div>
					<div class="name">id centralina</div><div>${deviceId}</div>
				</div>`;
	}


	let freeDevices = devices.filter(device => !isDeviceAssociatedToASite(device, sites))
	let plants = []
	sites.forEach(a => {
		if (a.plants) plants.push(...a.plants)
	})

	let markers = [];
	let center = {
		lat: 40.967320,
		lng: 17.115670
	};

	sites.forEach(k => {
		if (k.lat && k.lng) markers.push({
			lat: k.lat,
			lng: k.lng,
			data: devicePopUp(k.device.id, k.device.deviceId),
			icon: pinIcon
		});
	});

	if (markers && markers.length > 0) {
		center = { lat: markers[0].lat, lng: markers[0].lng };
	}


	let tableHeaderSite = [
		{
			Header: "ID",
			accessor: 'id',
			Cell: props => <div>{props.original.device.id}</div>
		},
		{
			Header: "Centralina associata",
			accessor: 'device',
			Cell: props => <div>{props.original.device.deviceId}</div>
		},
		{
			Header: "Label",
			accessor: 'label',
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span
					style={{paddingRight: "0.5rem"}}>{props.original.device.label}</span>
				{roles.isAdmin(user) && <Image
					src={editIcon}
					hover={editIcon_over}
					onClick={() => setDeviceModalOpen(props.original.device.deviceId)}
				/>}
			</Row>
		},
		{
			Header: "Impianti associati",
			accessor: 'plants',
			Cell: props => <Column>
				{props.original.plants?
					props.original.plants.map(a => <Row>{`${a.id} - ${a.name}`}</Row>)
					: null
				}
			</Column>
		},
		{
			Header: "Capacità",
			accessor: 'capacity',
			Cell: props => <div>{`${+props.original.capacity || "-"} kWp`}</div>
		},
		{
			Cell: props => <div>
				{roles.isAdmin(user) && <Row>
					<div data-tip="Modifica">
						<Image
							src={editIcon}
							hover={editIcon_over}
							onClick={() => {
								setSiteModalOpen(true)
								setSiteFields({
									id: props.original.device.id,
									plants: props.original.plants,
									capacity: props.original.capacity,
									lat: props.original.lat,
									lng: props.original.lng,
									city: props.original.city,
									address: props.original.address,
								})
							}}
						/>
					</div>
					&nbsp;&nbsp;
					<div data-tip="Elimina">
						<Image
							src={deleteIcon}
							hover={deleteIcon_over}
							onClick={() => {
								deleteSite(props.original.device.id)
							}}
						/>
					</div>
					<ReactTooltip effect='solid' />
				</Row>}
			</div>
		},
	]

	let tableHeaderDevice = [
		{
			Header: "ID",
			accessor: 'device',
			filterable: true,
			Cell: props => <Column>
				<Row style={{justifyContent: "space-around"}}>
					<span style={{paddingRight: "0.5rem"}}>{props.original.device}</span>
				</Row>
				{roles.isSuperUser(user) && <Row>
					<NTMButton primary onClick={() => disassociateDevice(props.original)}>Rimuovi</NTMButton>
				</Row>}
			</Column>
		},
		{
			Header: "Label",
			accessor: 'label',
			filterable: true,
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span
					style={{paddingRight: "0.5rem"}}>{props.original.label}</span>
				{roles.isAdmin(user) && <Image
					src={editIcon}
					hover={editIcon_over}
					onClick={() => setDeviceModalOpen(props.original.device)}
				/>}
			</Row>
		},
		{
			Header: "Ultima ril",
			accessor: 'receiptDate',
			Cell: props =>
				<div>{props.original.receiptDate ? moment(props.original.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Batteria",
			accessor: 'battery',
			Cell: props => <div>{`${+props.original.battery || "-"}%`}</div>
		},
		{
			Header: "Temperatura",
			accessor: 'soilTemperature',
			Cell: props => <div>{`${+props.original.soilTemperature || "-"}°`}</div>
		},
		{
			Header: "Radiazione solare",
			accessor: 'radiation',
			Cell: props => <div>{`${+props.original.radiation || "-"} W/mq`}</div>
		},
	]

	let tableHeaderPlant=[
		{
			Header: "ID",
			accessor: 'id',
			filterable: true,
			Cell: props => <div>{props.original.id}</div>
		},
		{
			Header: "Produttore",
			accessor: 'kind',
			filterable: true,
			Cell: props => <div>{props.original.kind}</div>
		},
		{
			Header: "ID Prod.",
			accessor: 'producerId',
			filterable: true,
			Cell: props => <div>{props.original.producerId || props.original.producerSerial || props.original.uuid}</div>
		},
		{
			Header: "Nome",
			accessor: 'name',
			filterable: true,
			Cell: props => <div>{props.original.name}</div>
		},
		{
			Header: "Capacità",
			accessor: 'capacity',
			Cell: props => <div>{`${+props.original.capacity || "-"} kWp`}</div>
		},
		{
			Header: "Data inst.",
			accessor: 'installationDate',
			Cell: props =>
				<div>{props.original.installationDate}</div>
		},
	]


	return (
		<div className={"animated fadeIn container"}>
			<Row className={style.box}>
				<HereMap
					center={center}
					zoom={10}
					theme={"normal.day"}
					markers={markers}
					className={style.map}
				/>
			</Row>

			<Column>
				{
					sites.length === 0 && <Row style={{color:"#D8232A", paddingBottom:"1rem", fontWeight:"bold"}}>Non è ancora stato associato nessun sito a questo progetto</Row>
				}
				{
					roles.isAdmin(user) && <Row>
						<NTMButton
							onClick={() => { setSiteFields(null); setSiteModalOpen(true) }}>
							<Image
								src={addIcon_top}
								hover={addIcon_topOver} />
							AGGIUNGI
						</NTMButton>
					</Row>
				}
				{
					sites.length > 0 &&
						<NTMTable
							title="Siti"
							header={tableHeaderSite}
							body={sites}
							defaultPageSize={5}
						/>
				}
				{
					devices.length === 0 && <Row style={{color:"#D8232A", paddingBottom:"1rem", fontWeight:"bold"}}>Non è ancora stato associato nessun dispositivo a questo progetto</Row>
				}
				{
					devices.length > 0 &&
						<NTMTable
							title="Centraline"
							header={tableHeaderDevice}
							body={devices}
							defaultPageSize={5}
						/>
				}
				{
					plants.length > 0 &&
						<NTMTable
							title="Impianti"
							header={tableHeaderPlant}
							body={plants}
							defaultPageSize={5}
						/>
				}
			</Column>

			{sites.length > 0 &&
				<WeatherWidget
					lat={sites[0].lat}
					lng={sites[0].lng}
					location={sites[0].device.deviceId}
					type="forecast"
				/>
			}
			<AssignDevicePanel
				devices={devices.map(device => device.device)}
				onSave={() => queryClient.invalidateQueries('devices')}
			/>

			<EditLabelModal
				isOpen={!!deviceModalOpen}
				onClose={() => setDeviceModalOpen(null)}
				onSubmit={changeLabel}
			/>
			<SolarSiteModal
				isOpen={siteModalOpen}
				onClose={() => setSiteModalOpen(false)}
				onSubmit={saveSite}
				siteFields={siteFields}
				freeDevices={freeDevices}
				freePlants={unassignedPlants}
			/>
		</div>
	);

}
